import { useMemo, useState, useCallback, useEffect, useRef } from "react";
import { FloatingHint } from "@rapid/components/FloatingHint";
import { useComputedValues } from "@rapid/contexts/ComputedValuesContext";
import { useGetPredictions } from "@rapid/hooks/useGetPredictions";
import { useFlowState } from "@rapid/hooks/useFlowState";
import { BuildSteps, Flows } from "@rapid/types/flows";
import Button from "@components/Button";
import { useAnalytics } from "@rapid/contexts/AnalyticsContext";
import { AnalyticsFlowEvent } from "@rapid/analytics/types";

const LABEL_THRESHOLD_TO_SHOW_HINT = 2;
const LABEL_INCREMENT_AFTER_DISMISS = 3;

export const AnnotateHint = () => {
    const { currentFrameAnnotations } = useComputedValues();
    const [dismissedAtCount, setDismissedAtCount] = useState(0);
    const { generateModel } = useGetPredictions();
    const { setStep } = useFlowState();
    const [isLoading, setIsLoading] = useState(false);
    const { track } = useAnalytics();
    const hasTrackedRef = useRef(false);

    const maxUserDrawnCount = useMemo(() => {
        if (!currentFrameAnnotations || currentFrameAnnotations.length === 0) {
            return 0;
        }

        // Count only user-drawn annotations per label (no confidence = user-drawn)
        const labelCounts: Record<string, number> = {};
        for (const annotation of currentFrameAnnotations) {
            const isUserDrawn = !annotation.confidence;
            if (annotation.label && isUserDrawn) {
                labelCounts[annotation.label] = (labelCounts[annotation.label] || 0) + 1;
            }
        }

        // Return the max count across all labels
        const counts = Object.values(labelCounts);
        return counts.length > 0 ? Math.max(...counts) : 0;
    }, [currentFrameAnnotations]);

    // Show hint if any label has more than threshold user-drawn annotations beyond the dismissed threshold
    const shouldShowHint =
        maxUserDrawnCount > LABEL_THRESHOLD_TO_SHOW_HINT && maxUserDrawnCount > dismissedAtCount;

    // Track when hint is shown
    useEffect(() => {
        if (shouldShowHint && !hasTrackedRef.current) {
            track({
                event: AnalyticsFlowEvent.EXCESSIVE_LABELLING_HINT_SHOWN,
                data: { labelCount: maxUserDrawnCount }
            });
            hasTrackedRef.current = true;
        } else if (!shouldShowHint) {
            hasTrackedRef.current = false;
        }
    }, [shouldShowHint, maxUserDrawnCount, track]);

    const handleDismiss = useCallback(() => {
        setDismissedAtCount(maxUserDrawnCount + LABEL_INCREMENT_AFTER_DISMISS);
    }, [maxUserDrawnCount]);

    const handleGoToReview = useCallback(async () => {
        setIsLoading(true);
        try {
            await generateModel();
            setStep({ flow: Flows.BUILD, step: BuildSteps.REVIEW });
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    }, [generateModel, setStep]);

    if (!shouldShowHint) {
        return null;
    }

    const hintContent = (
        <div className="flex items-center gap-3">
            <span>You only need 1-2 labels per object — we'll find the rest for you!</span>
            <Button
                type="dark-secondary"
                size="small"
                onClick={handleGoToReview}
                disabled={isLoading}
                loading={isLoading}
            >
                Go to Review
            </Button>
        </div>
    );

    return (
        <FloatingHint
            isVisible={shouldShowHint}
            message={hintContent}
            isDismissable={true}
            lightMode={false}
            onDismiss={handleDismiss}
        />
    );
};
